import React, {useState} from "react";
import styles from "./AiTooltip.module.css";
import IconClose from "../../../assets/icon-close.svg";
import IconPokerlyAI from "../../../assets/icon-pokerly-ai.svg";
import {useTranslation} from "react-i18next";
import {VoteParser} from "utils/VoteParser";
import {SpreadLevel} from "utils/VoteStats";
import {Link} from "react-router-dom";


export interface AiTooltipProps {
    recommendedValue?: string | number;
    explanationText: string;
    average?: number;
    median?: string | number;
    spread?: SpreadLevel;
    range?: number | string | null | undefined;
    className?: string;
    classes?: {
        header?: string;
        headerIcon?: string;
        headerTitle?: string;
        recommendation?: string;
        description?: string;
        stats?: string;
        statItem?: string;
        closeButton?: string;
    };
}

const AiTooltip: React.FC<AiTooltipProps> = ({
                                                 recommendedValue,
                                                 explanationText,
                                                 average,
                                                 median,
                                                 spread,
                                                 className,
                                                 classes = {},
                                                 range
                                             }) => {

    const {t} = useTranslation();
    const [visible, setVisible] = useState(true);
    const SpreadNumber = VoteParser.formatMaybeNumber(range);
    if (!visible) return null;

    return (
        <div className={[styles.tooltip, className].filter(Boolean).join(" ")}>

            <div className={[styles.tooltipRecommendation, classes.recommendation].filter(Boolean).join(" ")}>
                <span></span>
                {t("recommendedSP")} <b>{VoteParser.formatMaybeNumber(recommendedValue)}</b>
            </div>
            {spread == 'low' && (
                <div className={`${styles.tooltipRecommendation} ${styles.lowSpread}`}>
                    <span></span>
                    {t("lowSpread")} <b>{SpreadNumber}</b>
                </div>
            )}
            {spread == 'medium' && (
                <div className={`${styles.tooltipRecommendation} ${styles.mediumSpread}`}>
                    <span></span>
                    {t("mediumSpread")} <b>{SpreadNumber}</b>
                </div>
            )}
            {spread == 'high' && (
                <div className={`${styles.tooltipRecommendation} ${styles.highSpread}`}>
                    <span></span>
                    {t("highSpread")} <b>{SpreadNumber}</b>
                </div>
            )}
            {(average != null || median != null) && (
                <div className={[styles.tooltipStats, classes.stats].filter(Boolean).join(" ")}>
                    {median != null && (
                        <div className={[styles.tooltipStatItem, classes.statItem].filter(Boolean).join(" ")}>
                            {t("medianEstimate")}: <b>{VoteParser.formatMaybeNumber(median)}</b>
                        </div>
                    )}
                    <div>
                        <b>·</b>
                    </div>
                    {average != null && (
                        <div className={[styles.tooltipStatItem, classes.statItem].filter(Boolean).join(" ")}>
                            {t("averageEstimate")}: <b>{VoteParser.formatMaybeNumber(average)}</b>
                        </div>
                    )}
                </div>
            )}
            <div className={[styles.tooltipDescription, classes.description].filter(Boolean).join(" ")}>
                {explanationText}
            </div>
            <div className={[styles.tooltipFooter, classes.header].filter(Boolean).join(" ")}>
                <div className={styles.tooltipLogo}>
                    <svg width="11" height="12" viewBox="0 0 11 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                            d="M2.83301 1.68796L5.08301 2.98796L7.33301 1.68796M2.83301 9.47796V6.88296L0.583008 5.58296M9.58301 5.58296L7.33301 6.88296V9.47796M0.718008 3.06296L5.08301 5.58796L9.44801 3.06296M5.08301 10.623V5.58296M9.58301 7.58296V3.58296C9.58283 3.4076 9.53654 3.23536 9.44878 3.08354C9.36102 2.93172 9.23488 2.80564 9.08301 2.71796L5.58301 0.717959C5.43099 0.630191 5.25854 0.583984 5.08301 0.583984C4.90747 0.583984 4.73503 0.630191 4.58301 0.717959L1.08301 2.71796C0.931139 2.80564 0.804998 2.93172 0.717239 3.08354C0.629479 3.23536 0.583188 3.4076 0.583008 3.58296V7.58296C0.583188 7.75832 0.629479 7.93055 0.717239 8.08238C0.804998 8.2342 0.931139 8.36028 1.08301 8.44796L4.58301 10.448C4.73503 10.5357 4.90747 10.5819 5.08301 10.5819C5.25854 10.5819 5.43099 10.5357 5.58301 10.448L9.08301 8.44796C9.23488 8.36028 9.36102 8.2342 9.44878 8.08238C9.53654 7.93055 9.58283 7.75832 9.58301 7.58296Z"
                            stroke="#484848" stroke-width="1.16667" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                    <div className={[styles.tooltipTitle, classes.headerTitle].filter(Boolean).join(" ")}>
                        Pokerly AI β
                    </div>
                </div>
                <div className={styles.help}>
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                            d="M6 12C7.5913 12 9.11742 11.3679 10.2426 10.2426C11.3679 9.11742 12 7.5913 12 6C12 4.4087 11.3679 2.88258 10.2426 1.75736C9.11742 0.632141 7.5913 0 6 0C4.4087 0 2.88258 0.632141 1.75736 1.75736C0.632141 2.88258 0 4.4087 0 6C0 7.5913 0.632141 9.11742 1.75736 10.2426C2.88258 11.3679 4.4087 12 6 12ZM6.6975 4.941L5.9475 8.46975C5.895 8.72475 5.96925 8.8695 6.1755 8.8695C6.321 8.8695 6.54075 8.817 6.69 8.685L6.624 8.997C6.40875 9.2565 5.934 9.4455 5.52525 9.4455C4.998 9.4455 4.77375 9.129 4.91925 8.45625L5.47275 5.85525C5.52075 5.6355 5.47725 5.556 5.2575 5.50275L4.91925 5.442L4.98075 5.15625L6.6975 4.941ZM6 4.125C5.80109 4.125 5.61032 4.04598 5.46967 3.90533C5.32902 3.76468 5.25 3.57391 5.25 3.375C5.25 3.17609 5.32902 2.98532 5.46967 2.84467C5.61032 2.70402 5.80109 2.625 6 2.625C6.19891 2.625 6.38968 2.70402 6.53033 2.84467C6.67098 2.98532 6.75 3.17609 6.75 3.375C6.75 3.57391 6.67098 3.76468 6.53033 3.90533C6.38968 4.04598 6.19891 4.125 6 4.125Z"
                            fill="#9865DA"/>
                    </svg>
                    <Link to="/points" target="_blank">Как работает оценка?</Link>
                </div>
            </div>
        </div>
    );
};

export default AiTooltip;
